import { DestroyRef, Injectable, Injector, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { ModalPopupMessages } from '../utility/constants';
import { SnakbarComponent } from '../components/snakbar/snakbar.component';
import { NotificationSnackbarComponent } from '../components/notification-snackbar/notification-snackbar.component';
import { CommonService } from './common.service';

@Injectable({
  providedIn: 'root',
})
export class SnakbarService {
  private readonly snackbarDuration = 10000;
  private readonly destroyRef = inject(DestroyRef);
  private timerSubscription: Subscription = new Subscription();
  private animationTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly injector: Injector
  ) {
    this.destroyRef.onDestroy(() => {
      if (this.animationTimer) {
        clearTimeout(this.animationTimer);
      }
    });
  }

  public openSnackBar(message: any): void {
    this.clearTimer();
    this.setOverlayValue(true);
    const config: MatSnackBarConfig = {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'snackbar',
      duration: this.snackbarDuration,
    };
    const snackBarRef = this.snackBar.openFromComponent(SnakbarComponent, {
      ...config,
      data: {
        message,
        closeIcon: 'custom_close',
      },
    });

    this.timerSubscription = snackBarRef
      .afterDismissed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearTimer();
        this.setOverlayValue(false);
      });

    this.addClassToSnackbarContainer('show-error'); // animate snackbar
  }

  public openCustomisedSnackBar(
    message: string,
    type: string,
    okButton?: boolean,
  ): void {
    this.clearTimer();
    this.setOverlayValue(true);
    const config: MatSnackBarConfig = {
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass:
        type === ModalPopupMessages.success
          ? 'success-snackbar'
          : type === 'error'
            ? 'error-snackbar'
            : 'warning-snackbar',
      duration: this.snackbarDuration,
    };

    const snackBarRef = this.snackBar.openFromComponent(
      NotificationSnackbarComponent,
      {
        ...config,
        data: {
          message,
          type,
          okButton,
        },
      },
    );

    this.timerSubscription = snackBarRef
      .afterDismissed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearTimer();
        this.setOverlayValue(false);
      });

    this.addClassToSnackbarContainer('show'); // animate snackbar
  }

  private clearTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
      this.animationTimer = undefined;
    }
  }

  private setOverlayValue(isOpen: boolean) {
    this.injector.get(CommonService).setOverlayOpen(isOpen);
  }

  private addClassToSnackbarContainer(className: string) {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
    }
    this.animationTimer = setTimeout(() => {
      this.animationTimer = undefined;
      const container = document.querySelector('.mat-mdc-snack-bar-container');
      if (container) {
        container.classList.add(className);
      }
    }, 300);
  }
}
